import React, { useState, useMemo, useEffect } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  Typography,
  Box,
  Alert,
  Button,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ButtonBase,
} from '@mui/material';
import {
  Inventory,
  CheckCircle,
  Warning,
  Search,
  Refresh,
  Visibility,
  LocationOn,
  Error,
  Add,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { WarehouseAPI } from '../services/api';
import { InventoryItem, BundleSearchResult } from '../types';
import { statusAccent, compare, InventorySortKey, SortDir } from '../utils/inventoryStatus';
import { formatDate } from '../utils/formatDate';
import BundleSelector from '../components/BundleSelector';
import { useDebounce } from '../hooks/useDebounce';
import { useUrlParam, useSetUrlParams } from '../components/common/useUrlParam';
import { usePermission } from '../hooks/usePermission';
import { display, eyebrow } from '../components/actionables/brand';
import TablePaginationBar, { DEFAULT_PAGE_SIZE } from '../components/common/TablePaginationBar';
import PageHeader from '../components/common/PageHeader';
import StatPill from '../components/common/StatPill';
import SegmentedFilter from '../components/common/SegmentedFilter';
import ListRowCard from '../components/common/ListRowCard';

// Page size is the canonical shared default (25) from TablePaginationBar — the whole tool pages at
// one size. Kept as a local alias so the two existing use sites (the slice memo and the pager's
// rowsPerPage) stay unchanged.
const INVENTORY_PAGE_SIZE = DEFAULT_PAGE_SIZE;

// Sort state, status accents, severity rank, and the row comparator now come from
// utils/inventoryStatus (shared, unit-tested) — see the imports above. The sort key 'updated' is
// 'lastUpdated' in the shared type.

// Shared column tracks for the inventory list header row AND every data row, so all fields
// line up in fixed vertical columns (SKU grows to fill; the rest are fixed-width). The same
// template drives the header labels and the rows, which is what keeps them perfectly aligned.
// On small screens we collapse to a simpler stacked layout via the `xs` breakpoint.
const ROW_GRID = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr auto',
    md: 'minmax(200px, 2fr) 120px 140px minmax(150px, 1fr) 56px',
  },
  alignItems: 'center',
  columnGap: 2,
} as const;

const InventoryPage: React.FC = () => {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  // RBAC: the inventory page is read-first; the mutate affordances (Create Bundle shortcut
  // and the inactive-bundle Debundle action) are gated on the view-inventory edit
  // capability. The destination pages + the backend enforce their own guards (SI-5).
  const canEdit = usePermission('view-inventory').can('edit');
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [bundleSelectorOpen, setBundleSelectorOpen] = useState(false);
  const [selectedBundle, setSelectedBundle] = useState<BundleSearchResult | null>(null);

  // Filters/search/sort/page live in the URL (prefix "iv") so a refresh restores exactly where
  // the user left off. Defaults stay OUT of the URL. Changing a filter/search/sort resets the
  // page to 0 in the SAME update (via useSetUrlParams / the alsoClear arg).
  const setUrlParams = useSetUrlParams();
  // The search box stays controlled on `searchTerm`; the DEBOUNCED value is what gets written to
  // the URL (seeded from "ivq" on mount) so typing doesn't rewrite the URL on every keystroke.
  const [ivq] = useUrlParam('ivq', '');
  const [searchTerm, setSearchTerm] = useState(ivq);
  const [statusFilter] = useUrlParam('ivstatus', 'all');
  const [sortKeyParam] = useUrlParam('ivsort', 'sku');
  const [sortDirParam] = useUrlParam('ivdir', 'asc');
  const sortKey = sortKeyParam as InventorySortKey;
  const sortDir = sortDirParam as SortDir;
  const [pageParam, setPageParam] = useUrlParam('ivpage', '0');
  const page = parseInt(pageParam, 10) || 0;
  const setPage = (v: number) => setPageParam(v === 0 ? '0' : String(v));

  // Debounce search term with 800ms delay
  const debouncedSearchTerm = useDebounce(searchTerm, 800);
  // Push the debounced value into the URL (resetting the page in the same update). Seeding the
  // input from the URL above means an initial match is a no-op write.
  useEffect(() => {
    if (debouncedSearchTerm === ivq) return;
    setUrlParams({ ivq: debouncedSearchTerm === '' ? null : debouncedSearchTerm, ivpage: null });
  }, [debouncedSearchTerm, ivq, setUrlParams]);

  // Fetch inventory data - load all on page load, or search when term provided
  const { data: inventoryResponse, isLoading, isError, refetch } = useQuery({
    queryKey: ['inventory-items', debouncedSearchTerm],
    queryFn: async () => {
      // Use the search term if provided (3+ chars), otherwise fetch all with empty string
      const searchValue = debouncedSearchTerm.trim().length >= 3
        ? debouncedSearchTerm.trim()
        : ''; // Empty string to fetch all inventory

      const response = await WarehouseAPI.getShelfWiseInventory(searchValue, 0, 500);

      // Transform ShelfInventoryItem[] to InventoryItem[]
      const transformedData = response.data.map(item => ({
        id: item.id,
        skuId: item.skuCode,
        skuName: item.skuCode, // Use skuCode as name since we don't have actual name
        quantity: item.quantity,
        shelfLocation: item.shelfCode,
        warehouseLocation: item.facilityCode,
        lastUpdated: item.updatedAt,
        status: item.quantity > 0 ? 'available' as const : 'out-of-stock' as const,
        reservedQuantity: 0, // Not available from API
        minThreshold: 10, // Default threshold
        maxThreshold: 100, // Default threshold
      }));

      return { data: transformedData };
    },
    enabled: true,
    staleTime: 30 * 1000, // Cache for 30 seconds
  });

  // Extract inventory items from response (no fallback to mock data)
  const inventoryItems = useMemo(() => inventoryResponse?.data || [], [inventoryResponse?.data]);

  // Filter inventory items by status only (search is handled by API)
  const filteredInventory = useMemo(() => {
    let filtered = inventoryItems;

    // Status filter only - search is handled by the API call
    if (statusFilter !== 'all') {
      filtered = filtered.filter(item => {
        if (statusFilter === 'low-stock') return item.quantity <= 20;
        if (statusFilter === 'out-of-stock') return item.quantity === 0;
        if (statusFilter === 'in-stock') return item.quantity > 20;
        return true;
      });
    }

    // Order rows by the active sort key + direction so the list has a stable, predictable
    // sequence instead of the backend's arbitrary return order. Default is SKU ascending (A→Z,
    // case-insensitive, numeric-aware), matching the prior fixed behaviour. Copy before sort —
    // never mutate the memoized source array. This runs BEFORE pagination slices the result.
    // (InventoryItem is assignable to the comparator's InventoryRowLike.)
    return [...filtered].sort((a, b) => compare(a, b, sortKey, sortDir));
  }, [inventoryItems, statusFilter, sortKey, sortDir]);

  // Page the filtered set client-side (mirrors AdminUsersList) — purely presentational.
  const pagedInventory = useMemo(
    () => filteredInventory.slice(page * INVENTORY_PAGE_SIZE, page * INVENTORY_PAGE_SIZE + INVENTORY_PAGE_SIZE),
    [filteredInventory, page],
  );

  // Status filter change — write the key (dropped when 'all') and reset the page in one update.
  const handleStatusFilter = (v: string) => {
    setUrlParams({ ivstatus: v === 'all' ? null : v, ivpage: null });
  };

  // Clicking a column toggles direction when it is already the active key; otherwise it switches
  // to that key with a sensible initial direction (ascending for text/status, descending for
  // quantity/date so the "most" of each lands first). Both key and dir plus the page reset go
  // through a single URL update so they never clobber each other.
  const handleSort = (key: InventorySortKey) => {
    let nextKey = sortKey;
    let nextDir: SortDir;
    if (key === sortKey) {
      nextDir = sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      nextKey = key;
      nextDir = key === 'quantity' || key === 'lastUpdated' ? 'desc' : 'asc';
    }
    setUrlParams({
      ivsort: nextKey === 'sku' ? null : nextKey,
      ivdir: nextDir === 'asc' ? null : nextDir,
      ivpage: null,
    });
  };

  // Calculate summary statistics
  const inventoryStats = useMemo(() => {
    const totalItems = inventoryItems.length;
    const inStock = inventoryItems.filter(item => item.quantity > 20).length;
    const lowStock = inventoryItems.filter(item => item.quantity > 0 && item.quantity <= 20).length;
    const outOfStock = inventoryItems.filter(item => item.quantity === 0).length;
    const totalQuantity = inventoryItems.reduce((sum, item) => sum + item.quantity, 0);

    return { totalItems, inStock, lowStock, outOfStock, totalQuantity };
  }, [inventoryItems]);

  const getStockStatusColor = (quantity: number) => {
    if (quantity === 0) return 'error';
    if (quantity <= 20) return 'warning';
    return 'success';
  };

  const getStockStatusLabel = (quantity: number) => {
    if (quantity === 0) return 'Out of Stock';
    if (quantity <= 20) return 'Low Stock';
    return 'In Stock';
  };

  const handleViewDetails = (item: InventoryItem) => {
    setSelectedItem(item);
    setDetailsOpen(true);
  };

  const handleBundleSelect = (bundle: BundleSearchResult) => {
    setSelectedBundle(bundle);
    // You can add navigation to bundle creation page or show bundle details
    // Example: navigate('/bundle-creation', { state: { bundle } });
  };

  // Clickable column-header cell for the card-row list. Uses real button semantics for keyboard
  // access, carries aria-sort on the active column, and shows a direction arrow only when active.
  const SortableHeader: React.FC<{ label: string; sortKeyFor: InventorySortKey }> = ({ label, sortKeyFor }) => {
    const active = sortKey === sortKeyFor;
    const ariaSort: 'ascending' | 'descending' | 'none' = active
      ? (sortDir === 'asc' ? 'ascending' : 'descending')
      : 'none';
    return (
      <Box role="columnheader" aria-sort={ariaSort} sx={{ minWidth: 0 }}>
        <ButtonBase
          onClick={() => handleSort(sortKeyFor)}
          aria-label={`Sort by ${label}, ${active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'ascending'}`}
          sx={{
            ...eyebrow,
            color: active ? '#1A1410' : '#8A8078',
            fontSize: '0.6rem',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.25,
            borderRadius: '4px',
            px: 0.25,
            '&:hover': { color: '#1A1410' },
            '&:focus-visible': { outline: '2px solid #DC5829', outlineOffset: 2 },
          }}
        >
          {label}
          {active && (
            sortDir === 'asc'
              ? <ArrowUpward sx={{ fontSize: 12 }} />
              : <ArrowDownward sx={{ fontSize: 12 }} />
          )}
        </ButtonBase>
      </Box>
    );
  };

  // Segmented status filter — maps to the existing statusFilter values, preserving
  // filteredInventory semantics exactly (in-stock = qty > 20, low = qty <= 20, out = qty === 0).
  const statusOptions: Array<{ value: string; label: string }> = [
    { value: 'all', label: 'All' },
    { value: 'in-stock', label: 'In Stock' },
    { value: 'low-stock', label: 'Low' },
    { value: 'out-of-stock', label: 'Out' },
  ];

  return (
    <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, pt: 2, pb: 3 }}>
      {/* Header */}
      <PageHeader
        icon={<Inventory sx={{ color: '#DC5829', fontSize: '1.9rem' }} />}
        title="Inventory"
        subtitle="Manage stock levels and monitor inventory status"
        actions={
          <>
            {canEdit && (
              <Button
                variant="contained"
                disableElevation
                startIcon={<Add />}
                onClick={() => navigate('/create-bundle')}
                sx={{
                  py: 1,
                  px: 2.5,
                  fontWeight: 800,
                  textTransform: 'none',
                  borderRadius: '999px',
                  '&:hover': {
                    boxShadow: '0px 4px 12px rgba(220, 88, 41, 0.3)',
                  },
                }}
              >
                Create Bundle
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => refetch()}
              disabled={isLoading}
              sx={{
                py: 1,
                px: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: '999px',
              }}
            >
              {isLoading ? 'Loading...' : 'Refresh'}
            </Button>
          </>
        }
      />

      {/* Summary stat pills */}
      <Box
        component={motion.div}
        initial={reduceMotion ? false : { opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
        sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2.5 }}
      >
        <StatPill variant="summary" label="Total Items" value={inventoryStats.totalItems} accent="#DC5829" icon={<Inventory fontSize="small" />} />
        <StatPill variant="summary" label="In Stock" value={inventoryStats.inStock} accent="#16A34A" icon={<CheckCircle fontSize="small" />} />
        <StatPill variant="summary" label="Low Stock" value={inventoryStats.lowStock} accent="#D97706" icon={<Warning fontSize="small" />} />
        <StatPill variant="summary" label="Out of Stock" value={inventoryStats.outOfStock} accent="#DC2626" icon={<Error fontSize="small" />} />
      </Box>

      {/* Toolbar: search + segmented status filter + total-quantity pill */}
      <Box
        component={motion.div}
        initial={reduceMotion ? false : { opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}
      >
        <TextField
          size="small"
          placeholder="Search by SKU code…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 260, flex: '1 1 260px', '& .MuiOutlinedInput-root': { borderRadius: '999px', bgcolor: '#fff' } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: '#8A8078' }} />
              </InputAdornment>
            ),
          }}
        />
        <SegmentedFilter options={statusOptions} value={statusFilter} onChange={handleStatusFilter} layoutId="inventory-status-active" />
        <Box sx={{ ml: 'auto' }}>
          <StatPill label="Total Quantity" value={inventoryStats.totalQuantity} accent="#1A1410" />
        </Box>
      </Box>

      {/* Inventory list */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography sx={{ ...display('1.1rem'), color: 'text.primary' }}>
          Inventory Items ({filteredInventory.length})
        </Typography>
      </Box>

      {isLoading ? (
        <Box
          sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            textAlign: 'center', py: 8, px: 3, borderRadius: '14px', bgcolor: 'background.paper',
            border: '1px dashed #ECECE4',
          }}
        >
          <CircularProgress sx={{ color: '#DC5829' }} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading inventory…
          </Typography>
        </Box>
      ) : isError ? (
        <Box
          sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            textAlign: 'center', py: 8, px: 3, borderRadius: '14px', bgcolor: 'background.paper',
            border: '1px dashed #ECECE4',
          }}
        >
          <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: 'rgba(220,38,38,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
            <Error sx={{ color: '#DC2626' }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
            Couldn't load inventory
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
            The inventory service may be unavailable. Please try again.
          </Typography>
          <Button
            variant="contained"
            disableElevation
            startIcon={<Refresh />}
            onClick={() => refetch()}
            sx={{
              py: 1,
              px: 2.5,
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: '999px',
            }}
          >
            Retry
          </Button>
        </Box>
      ) : filteredInventory.length === 0 ? (
        <Box
          sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            textAlign: 'center', py: 8, px: 3, borderRadius: '14px', bgcolor: 'background.paper',
            border: '1px dashed #ECECE4',
          }}
        >
          <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: searchTerm.length >= 3 ? '#f4f1ea' : 'rgba(220,88,41,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
            {searchTerm.length >= 3
              ? <Search sx={{ color: '#8A8078' }} />
              : <Inventory sx={{ color: '#DC5829' }} />}
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
            {searchTerm.length >= 3
              ? 'No inventory items found'
              : 'No inventory data available'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {searchTerm.length >= 3
              ? 'Try searching with a different SKU code or check spelling'
              : 'There is no inventory to display right now.'}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Column header row — shares ROW_GRID with the data rows so labels sit exactly
              above their values. Hidden on xs where rows use the compact stacked layout. */}
          <Box
            role="row"
            sx={{
              ...ROW_GRID,
              display: { xs: 'none', md: 'grid' },
              px: 3, pb: 0.5,
            }}
          >
            <SortableHeader label="SKU" sortKeyFor="sku" />
            <SortableHeader label="Quantity" sortKeyFor="quantity" />
            <SortableHeader label="Status" sortKeyFor="status" />
            <SortableHeader label="Last Updated" sortKeyFor="lastUpdated" />
            <Box />
          </Box>
          {pagedInventory.map((item, index) => {
            const accent = statusAccent(item.quantity);
            return (
              <ListRowCard
                key={`${item.skuId}-${item.shelfLocation}-${index}`}
                accent={accent.main}
                index={index}
              >
                <Box sx={{ ...ROW_GRID, pl: 3, pr: 2, py: 1.25, rowGap: 0.5 }}>
                  {/* SKU + shelf */}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.3 }} noWrap>
                      {item.skuId}
                    </Typography>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.25, color: 'text.secondary', maxWidth: '100%' }}>
                      <LocationOn sx={{ fontSize: 15, flexShrink: 0 }} />
                      <Typography variant="caption" sx={{ fontWeight: 600 }} noWrap>
                        {item.shelfLocation || 'Unassigned'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Quantity */}
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 800, color: accent.main, lineHeight: 1.2,
                      justifySelf: { xs: 'end', md: 'start' }, gridColumn: { xs: '2', md: 'auto' },
                    }}
                  >
                    {item.quantity}
                  </Typography>

                  {/* Status pill */}
                  <Box sx={{ justifySelf: 'start', display: { xs: 'none', md: 'block' } }}>
                    <Chip
                      label={getStockStatusLabel(item.quantity)}
                      size="small"
                      sx={{ fontWeight: 700, borderRadius: '999px', bgcolor: accent.soft, color: accent.main }}
                    />
                  </Box>

                  {/* Last updated */}
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary', display: { xs: 'none', md: 'block' } }}
                    noWrap
                  >
                    {formatDate(item.lastUpdated)}
                  </Typography>

                  {/* Actions */}
                  <Box sx={{ justifySelf: 'end' }}>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        aria-label="View details"
                        onClick={() => handleViewDetails(item)}
                        sx={{ color: '#DC5829', bgcolor: 'rgba(220,88,41,0.08)', '&:hover': { bgcolor: 'rgba(220,88,41,0.16)' } }}
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </ListRowCard>
            );
          })}
          <TablePaginationBar
            page={page}
            total={filteredInventory.length}
            rowsPerPage={INVENTORY_PAGE_SIZE}
            onPageChange={setPage}
          />
        </Box>
      )}

      {/* Item Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Inventory Item Details
        </DialogTitle>
        <DialogContent>
          {selectedItem && (
            <Box>
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: 2
              }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    SKU ID
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedItem.skuId}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Shelf Location
                  </Typography>
                  <Typography variant="body1">
                    {selectedItem.shelfLocation || 'Unassigned'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Current Quantity
                  </Typography>
                  <Typography
                    variant="h5"
                    color={selectedItem.quantity === 0 ? 'error.main' : selectedItem.quantity <= 20 ? 'warning.main' : 'success.main'}
                  >
                    {selectedItem.quantity}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={getStockStatusLabel(selectedItem.quantity)}
                    color={getStockStatusColor(selectedItem.quantity) as any}
                  />
                </Box>
                <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Last Updated
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(selectedItem.lastUpdated)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDetailsOpen(false)}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '999px' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bundle Selector Dialog */}
      <BundleSelector
        open={bundleSelectorOpen}
        onClose={() => setBundleSelectorOpen(false)}
        onBundleSelect={handleBundleSelect}
      />

      {/* Selected Bundle Alert (for demo) */}
      {selectedBundle && (
        <Alert
          severity="success"
          onClose={() => setSelectedBundle(null)}
          sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1000 }}
        >
          Bundle selected: {selectedBundle.bundleSku} with {selectedBundle.products.length} products
        </Alert>
      )}
    </Box>
  );
};

export default InventoryPage;
